import { EventEmitter, Component, Output, Input, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { AbstractControl, ValidationErrors, Validators, FormControl } from '@angular/forms';
import { MatRadioChange } from '@angular/material/radio';
import { TranslateService } from '@ngx-translate/core';
import { IStockFileEntry, IStockFolder, IxellAPIService } from 'src/app/tools/api.service';
import { FileEditModalResult, GroupedLocale } from '../cloud/cloud.component';
import { checkForExtension, CLOUD_MAX_FILE_SIZE, MIN_COUNTRY_MODE } from '../utils/utils';


@Component({
    selector: 'cloud-edit-modal',
    templateUrl: './edit-modal.component.html',
    styleUrls: ['./edit-modal.component.scss']
})

export class CloudEditModalComponent {
    CLOUD_MAX_FILE_SIZE = CLOUD_MAX_FILE_SIZE;
    MIN_COUNTRY_MODE = MIN_COUNTRY_MODE;

    @Input() file: IStockFileEntry;
    @Input() folder: IStockFolder;
    @Input() possibleLocales: { [key: number]: GroupedLocale };
    @Input() uploadProgress: number;
    @Input() selectedCountry: number;
    @Input() selectedLanguage: number;
    @Output() close = new EventEmitter<void>();
    @Output() save = new EventEmitter<FileEditModalResult>();
    @Output() reset = new EventEmitter<void>();

    @ViewChildren('fileInputList') fileInputList!: QueryList<ElementRef<HTMLInputElement>>[];

    language: string;

    private readonly fileValidator = (idx: number) => async (control: AbstractControl): Promise<ValidationErrors | null> => {
        if (!this.selectedFiles[idx])
            return null;

        if (this.selectedFiles[idx].size > CLOUD_MAX_FILE_SIZE)
            return { fileTooBig: true };

        if (!await checkForExtension(this.selectedFiles[idx], this.folder.extensions))
            return { invalidExtension: true };

        return null;
    };

    readonly selectedCountries = [75, 75];
    readonly selectedLanguages = [1, 2];
    readonly selectedFiles: File[] = [null, null];
    readonly selectedFilenames = [new FormControl<string>('', [Validators.required], [this.fileValidator(0)]), new FormControl<string>('', [Validators.required], [this.fileValidator(1)])];
    readonly chosenTitles = [new FormControl<string>('', [Validators.required]), new FormControl<string>('', [Validators.required])];
    readonly fileKind = [1, 1];

    formsDisabled: boolean = false;
    formsGlobalError: string = '';

    constructor(private readonly apiService: IxellAPIService, private readonly translateService: TranslateService) { }

    ngOnInit() {
        if (this.folder.mode >= MIN_COUNTRY_MODE) {
            this.selectedCountries.pop();
            this.selectedLanguages.pop();
            this.selectedFiles.pop();
            this.selectedFilenames.pop();
            this.chosenTitles.pop();
            this.fileKind.pop();

            this.selectedCountries[0] = this.selectedCountry;
            this.selectedLanguages[0] = this.selectedLanguage;

            if (!this.getFileContent(this.file, this.folder.mode, 0)?.url)
                this.fileKind[0] = 0;
        }
        else {
            const idxToRemove = [];

            for (let i = 0; i < this.fileKind.length; i++) {
                const ctn = this.getFileContent(this.file, this.folder.mode, i);

                if (!ctn.content)
                    idxToRemove.push(i);
            }

            for (let i of idxToRemove) {
                this.selectedCountries.splice(i, 1);
                this.selectedLanguages.splice(i, 1);
                this.selectedFiles.splice(i, 1);
                this.selectedFilenames.splice(i, 1);
                this.chosenTitles.splice(i, 1);
                this.fileKind.splice(i, 1);
            }
        }

        for (let i = 0; i < this.fileKind.length; i++) {
            const ctn = this.getFileContent(this.file, this.folder.mode, i);

            this.chosenTitles[i].setValue(ctn.name);
        }
    }

    ngOnDestroy() {
    }

    getFileContent(file: IStockFileEntry, mode: number, idx: number) {
        return file.contents.find(ctn => (mode < MIN_COUNTRY_MODE || ctn.paysId === this.selectedCountries[idx]) && (mode >= MIN_COUNTRY_MODE || ctn.langId === this.selectedLanguages[idx]));
    }

    downloadFile(idx: number) {
        const ctn = this.getFileContent(this.file, this.folder.mode, idx);
        if (ctn)
            window.open(ctn.url, "_blank");
    }

    onFileSelected(fileInput: HTMLInputElement, i: number) {
        this.selectedFiles[i] = (fileInput.files.length > 0) ? fileInput.files[0] : null;
        this.selectedFilenames[i].setValue((fileInput.files.length > 0) ? fileInput.files[0].name : null);
        this.selectedFilenames[i].markAsTouched();
    }

    onFileReset(fileInput: HTMLInputElement) {
        fileInput.value = null;
        fileInput.dispatchEvent(new Event('change'));
    }

    getLanguage(locale: GroupedLocale, langId: number) {
        return locale.langues.find(l => l.idLangue == langId);
    }

    doClose() {
        this.close.emit();
    }

    doSave() {
        this.formsGlobalError = '';
        this.reset.emit();

        let bad = false;

        // Check for bad form inputs
        for (let i = 0; i < this.selectedLanguages.length; i++) {
            if (this.fileKind[i] === 1)
                this.selectedFilenames[i].markAsUntouched();

            if ((this.fileKind[i] !== 1 && this.selectedFilenames[i].invalid) || this.chosenTitles[i].invalid) {
                if (this.fileKind[i] !== 1)
                    this.selectedFilenames[i].markAsTouched();

                this.chosenTitles[i].markAsTouched();
                bad = true;
            }
        }

        if (bad)
            return;

        this.save.emit({
            file: this.file,
            folder: this.folder,
            data: this.selectedLanguages.map((_, i) => ({
                country: this.selectedCountries[i],
                language: this.selectedLanguages[i],
                title: this.chosenTitles[i].value,
                file: (this.fileKind[i] === 1) ? null : this.selectedFiles[i]
            })),
            formStatusChanger: (state: boolean | string) => this.changeFormState(state)
        });
    }

    changeFormState(state: boolean | string) {
        if (typeof (state) === 'string') {
            this.formsGlobalError = state;
            this.changeFormState(false);
            return;
        }

        this.formsDisabled = state;

        for (const selectedFilename of this.selectedFilenames) {
            if (state)
                selectedFilename.disable();
            else
                selectedFilename.enable();
        }

        for (const chosenTitle of this.chosenTitles) {
            if (state)
                chosenTitle.disable();
            else
                chosenTitle.enable();
        }
    }
}
